import { BaseWebComponentDataProvider } from '../../../framework/BaseWebComponentDataProvider';
import { DEFAULT_PAGE_NUMBER, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { Column, ColumnKeys, DEFAULT_PAGE_SIZE, IntegrationsGridViewData, License } from '../types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { Integration, IntegrationResponseStatus, IntegrationStatus, Sensor, SensorEnabledStatus } from '../../common/types';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { INTEGRATION_TO_FEATURE_MAP, IntegrationType } from '../../types/integration-type';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';

export default class IntegrationsGridViewDataProvider extends BaseWebComponentDataProvider {
  async getDataForView(): Promise<IntegrationsGridViewData> {
    const view: ViewConfiguration<Column, ColumnKeys> = this.getView();
    const licenses = await this.getCompanyLicenses();
    return {
      views: [view],
      data: {
        licenses,
      },
      allColumns: this.getAllColumns(),
      defaultViewProps: view,
      predefinedFilterKeys: [ColumnKeys.TYPE, ColumnKeys.COMPANY_NAME],
    };
  }

  async sensorSetEnabledStatus(sensor: Sensor, enabledStatus: SensorEnabledStatus): Promise<object> {
    return await this.dependencies.api.clients.Sensors.setEnabledStatus(
      { id: sensor._id, enabledStatus },
      getRequestDefaultOptions()
    );
  }

  async getCompanyLicenses(): Promise<Array<License>> {
    return await this.dependencies.api.clients.Licenses.getCompanyLicenses(
      { companyId: this.dependencies.getActiveCompany().companyId },
      getRequestDefaultOptions()
    );
  }

  async setEnabledStatus(integration: Integration, enabledStatus: SensorEnabledStatus): Promise<object> {
    return await this.dependencies.api.clients.SensorsIntegrations.setEnabledStatus(
      { id: integration._id, enabledStatus },
      getRequestDefaultOptions()
    );
  }

  async remove(integration: Integration): Promise<void> {
    await this.dependencies.api.clients.SensorsIntegrations.removeRecord(
      { id: integration._id },
      getRequestDefaultOptions()
    );
  }

  async deleteSensor(sensor: Sensor): Promise<void> {
    await this.dependencies.api.clients.Sensors.removeSensor({ id: sensor._id }, getRequestDefaultOptions());
  }

  private getView(): ViewConfiguration<Column, ColumnKeys> {
    const view = {
      id: 'integrations-grid',
      name: 'Default view',
      type: ViewType.DEFAULT,
      filters: [],
      displayedColumns: [Column.NAME, Column.TYPE, Column.STATUS, Column.RESPONSE_STATUS],
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
      compactView: false,
    };
    if (this.dependencies.isPartnerCompany()) {
      view.displayedColumns.splice(2, 0, Column.COMPANY_NAME);
    }
    return view;
  }

  private getAllColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    const stringsTranslator = BaseWebComponentTranslator.getInstance();
    const integrationTranslations = stringsTranslator.getSensorsIntegrationsStrings();
    let hasPlatformFlag;
    const columnValues: { label: string; value: string }[] = [];
    for (const integrationType of Object.keys(INTEGRATION_TO_FEATURE_MAP)) {
      hasPlatformFlag = this.dependencies.Ext.isFeatureAvailableForPlatform(
        INTEGRATION_TO_FEATURE_MAP[integrationType]
      );
      if (hasPlatformFlag) {
        columnValues.push({
          label: stringsTranslator.getTranslatedIntegrationNameByType(integrationType as unknown as IntegrationType),
          value: integrationType,
        });
      }
    }
    return [
      {
        column: Column.NAME,
        key: ColumnKeys.NAME,
        columnName: this.dependencies.translateFn('Name'),
        type: ColumnType.STRING,
        removable: false,
        filteringDisabled: true,
      },
      {
        column: Column.TYPE,
        key: ColumnKeys.TYPE,
        columnName: this.dependencies.translateFn('Type'),
        type: ColumnType.ENUM,
        removable: false,
        values: columnValues,
        flexGrow: 1,
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: this.dependencies.translateFn('Sensor status'),
        type: ColumnType.ENUM,
        removable: false,
        values: [
          {
            label: this.dependencies.translateFn('Active'),
            value: IntegrationStatus.ACTIVE,
          },
          {
            label: this.dependencies.translateFn('Action needed'),
            value: IntegrationStatus.ACTION_NEEDED,
          },
          {
            label: this.dependencies.translateFn('Disabled'),
            value: IntegrationStatus.DISABLED,
          },
          {
            label: this.dependencies.translateFn('Not licensed'),
            value: IntegrationStatus.NOT_LICENSED,
          },
        ],
        flexGrow: 1,
      },
      {
        column: Column.RESPONSE_STATUS,
        key: ColumnKeys.RESPONSE_STATUS,
        columnName: this.dependencies.translateFn('Response status'),
        type: ColumnType.ENUM,
        removable: false,
        values: [
          {
            label: this.dependencies.translateFn('Active'),
            value: IntegrationResponseStatus.ACTIVE,
          },
          {
            label: this.dependencies.translateFn('Unknown'),
            value: IntegrationResponseStatus.UNKNOWN,
          },
          {
            label: this.dependencies.translateFn('Needs attention'),
            value: IntegrationResponseStatus.NOT_AVAILABLE,
          },
          {
            label: this.dependencies.translateFn('Disabled'),
            value: IntegrationResponseStatus.DISABLED,
          },
        ],
        flexGrow: 1,
      },
      {
        column: Column.COMPANY_NAME,
        key: ColumnKeys.COMPANY_NAME,
        columnName: this.dependencies.translateFn('Company name'),
        type: ColumnType.STRING,
        removable: false,
      },
    ];
  }
}
